import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightLong,
  faCircleExclamation,
  faSpinner,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { listProviders, type ProviderListItemDto } from '../api/ame-platform.api';
import { planProviderMigration } from '../api/provider-migrations.api';

/**
 * Plan a provider migration — DRY RUN ONLY.
 *
 * Submits nothing but two provider ids. The server runs its own dry run and
 * derives the entire scope — eligible assets, exclusions, object count, byte
 * total and the item list — from the database. This form cannot influence what
 * is planned, and does not try to.
 *
 * The two client-side rules below (enabled + driverRegistered, and
 * source !== destination) are **usability filters, not safety mechanisms**. The
 * server independently refuses a same-provider migration, an unregistered or
 * disabled destination, and a plan with no migratable candidate. Nothing here
 * duplicates or weakens a server-side guard.
 *
 * Planning copies nothing. There is deliberately no execute, approve, flip,
 * rollback, cleanup, delete, deactivate or retry control anywhere in this file.
 */

export interface PlanMigrationFormProps {
  /** Called with the new migration id once the server has created the plan. */
  onPlanned: (migrationId: string) => void;
  onCancel: () => void;
}

export function PlanMigrationForm({ onPlanned, onCancel }: PlanMigrationFormProps) {
  const [providers, setProviders] = useState<ProviderListItemDto[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [source, setSource] = useState('');
  const [target, setTarget] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await listProviders();
        if (!cancelled) setProviders(rows);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load storage providers.');
        }
      } finally {
        if (!cancelled) setLoadingProviders(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Usability filter only. A provider that is disabled, or whose driver is not
   * registered in the backend, can never yield a plannable candidate — the
   * server blocks every asset with DESTINATION_UNAVAILABLE and then refuses the
   * plan outright. Hiding them spares the operator a guaranteed failure; it is
   * not what makes the operation safe.
   */
  const selectable = useMemo(
    () => providers.filter((p) => p.isEnabled && p.driverRegistered),
    [providers],
  );

  const sameProvider = source !== '' && source === target;
  const canSubmit = source !== '' && target !== '' && !sameProvider && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      // Exactly the two ids. Nothing about the scope is sent.
      const result = await planProviderMigration(source, target);
      onPlanned(result.migrationId);
    } catch (err) {
      // Surface the server's own words. No automatic retry, and no second plan
      // is attempted — a failed plan is a decision for the operator, not a loop.
      setError(
        err instanceof Error && err.message.length > 0
          ? err.message
          : 'The server refused to create this migration plan.',
      );
      setSubmitting(false);
    }
  };

  const label = (p: ProviderListItemDto) =>
    `${p.driverId.toUpperCase()} — ${p.name}${p.isActive ? ' (active)' : ''}`;

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="plan-migration-form">
      <h2 className="text-sm font-semibold text-foreground">Plan a provider migration</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Creates a <strong className="text-foreground">dry run</strong>: the server calculates
        exactly which objects would move.{' '}
        <strong className="text-foreground">Nothing is copied</strong>, no ownership changes, and
        execution requires a separate authorization.
      </p>

      {loadingProviders ? (
        <div
          className="mt-4 flex items-center gap-3 text-sm text-muted-foreground"
          data-testid="plan-providers-loading"
        >
          <FontAwesomeIcon icon={faSpinner} spin className="h-4 w-4" />
          Loading providers…
        </div>
      ) : (
        <>
          <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-end">
            <div>
              <label
                htmlFor="plan-source-provider"
                className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground"
              >
                Source provider
              </label>
              <select
                id="plan-source-provider"
                data-testid="plan-source-provider"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              >
                <option value="">Select source…</option>
                {selectable.map((p) => (
                  <option key={p.id} value={p.id}>
                    {label(p)}
                  </option>
                ))}
              </select>
            </div>

            <div className="hidden pb-2.5 text-muted-foreground sm:block">
              <FontAwesomeIcon icon={faArrowRightLong} className="h-4 w-4" />
            </div>

            <div>
              <label
                htmlFor="plan-target-provider"
                className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground"
              >
                Destination provider
              </label>
              <select
                id="plan-target-provider"
                data-testid="plan-target-provider"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
              >
                <option value="">Select destination…</option>
                {selectable.map((p) => (
                  <option key={p.id} value={p.id}>
                    {label(p)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {sameProvider && (
            <p
              className="mt-3 flex items-center gap-2 text-sm text-amber-600"
              data-testid="plan-same-provider-error"
            >
              <FontAwesomeIcon icon={faTriangleExclamation} className="h-3.5 w-3.5" />
              Source and destination must be different providers.
            </p>
          )}

          {error && (
            <div
              className="mt-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3"
              data-testid="plan-error"
            >
              <p className="flex items-start gap-2 text-sm text-destructive">
                <FontAwesomeIcon icon={faCircleExclamation} className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </p>
            </div>
          )}

          <div className="mt-5 flex items-center gap-3">
            <button
              type="button"
              data-testid="plan-submit"
              disabled={!canSubmit}
              onClick={() => void submit()}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting && <FontAwesomeIcon icon={faSpinner} spin className="h-3.5 w-3.5" />}
              {submitting ? 'Planning…' : 'Plan Migration'}
            </button>
            <button
              type="button"
              data-testid="plan-cancel"
              disabled={submitting}
              onClick={onCancel}
              className="rounded-lg border border-border px-4 py-2 text-sm text-foreground disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}
